import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FREE_MODE } from '../config/appConfig';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Building2, User, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { trackRegistration } from '../utils/analytics';

const Register = () => {
  const [searchParams] = useSearchParams();
  const defaultType = searchParams.get('type') === 'company' ? 'company' : 'individual';
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    userType: defaultType,
  });
  const [consents, setConsents] = useState({
    terms_accepted: false,
    privacy_accepted: false,
    marketing_accepted: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Walidacja email - pole obowiązkowe
    if (!formData.email || !formData.email.trim()) {
      setError('Email jest wymagany');
      return;
    }
    
    // Walidacja formatu email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Podaj poprawny adres email');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Hasła nie są identyczne');
      return;
    }
    
    if (formData.password.length < 6) {
      setError('Hasło musi mieć co najmniej 6 znaków');
      return;
    }
    
    if (!consents.terms_accepted) {
      setError('Akceptacja regulaminu jest wymagana');
      return;
    }
    if (!consents.privacy_accepted) {
      setError('Akceptacja polityki prywatności jest wymagana');
      return;
    }
    
    setLoading(true);
    try {
      await register(formData.email, formData.password, formData.name, formData.userType, consents);
      
      // Śledź konwersję rejestracji w Google Ads
      trackRegistration(formData.userType);
      
      toast.success('Konto zostało utworzone!', {
        description: 'Sprawdź swoją skrzynkę email (także folder SPAM) i oznacz nasze wiadomości jako "to nie spam".',
        duration: 8000
      });
      navigate(formData.userType === 'company' ? '/dashboard/company' : '/dashboard/individual');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12" data-testid="register-page">
      <SEO 
        title="Rejestracja"
        description="Załóż darmowe konto na Giełda Budowlana. Publikuj ogłoszenia, znajdź wykonawców lub zarejestruj swoją firmę budowlaną."
        canonical="/register"
      />
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <Link to="/" className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 bg-orange-500 rounded-md flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-bold text-lg text-slate-900">Giełda Budowlana</span>
          </Link>
          <CardTitle className="font-heading text-2xl">Utwórz konto</CardTitle>
          <CardDescription>Wybierz typ konta i rozpocznij</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={formData.userType} onValueChange={(value) => setFormData({ ...formData, userType: value })}>
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="individual" className="flex items-center gap-2" data-testid="tab-individual">
                <User className="w-4 h-4" />Indywidualny
              </TabsTrigger>
              <TabsTrigger value="company" className="flex items-center gap-2" data-testid="tab-company">
                <Building2 className="w-4 h-4" />Firma
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="individual">
              <div className="p-4 bg-green-50 rounded-lg mb-4">
                <h4 className="font-bold text-green-800 flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5" />Darmowe konto
                </h4>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>• Publikowanie ogłoszeń - bezpłatnie</li>
                  <li>• Odbieranie ofert od firm - bezpłatnie</li>
                  <li>• Wysyłanie wiadomości - bezpłatnie</li>
                </ul>
              </div>
            </TabsContent>
            <TabsContent value="company">
              <div className="p-4 bg-orange-50 rounded-lg mb-4">
                <h4 className="font-bold text-orange-800 flex items-center gap-2 mb-2">
                  <Building2 className="w-5 h-5" />Konto firmowe
                </h4>
                {FREE_MODE ? (
                  <ul className="text-sm text-orange-700 space-y-1">
                    <li>• Nielimitowane odpowiedzi na ogłoszenia</li>
                    <li>• Profil firmy z galerią zdjęć</li>
                    <li>• Bezpośredni kontakt z klientami</li>
                    <li className="font-bold text-green-700">• Wszystko za darmo!</li>
                  </ul>
                ) : (
                  <ul className="text-sm text-orange-700 space-y-1">
                    <li>• Stała subskrypcja: <strong>15 zł netto + VAT (18,45 zł brutto)</strong>/mies.</li>
                    <li>• Nielimitowane odpowiedzi na ogłoszenia</li>
                    <li>• Profil firmy z galerią zdjęć</li>
                    <li>• Bezpośredni kontakt z klientami</li>
                  </ul>
                )}
              </div>
            </TabsContent>
          </Tabs>

          {error && (
            <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              <AlertCircle className="w-4 h-4" />{error}
            </div>
          )}

          {/* Informacja o spamie - na górze dla lepszej widoczności na mobile */}
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
            <p className="text-sm text-amber-800 font-medium mb-1">📧 Ważna informacja o emailach</p>
            <p className="text-xs text-amber-700">
              Po rejestracji sprawdź swoją skrzynkę email, w tym folder <strong>SPAM</strong>. 
              Jeśli nasza wiadomość trafiła do spamu, oznacz ją jako „to nie spam" - 
              dzięki temu będziesz otrzymywać powiadomienia o ofertach i wiadomościach.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">{formData.userType === 'company' ? 'Nazwa firmy' : 'Imię i nazwisko'}</Label>
              <Input id="name" name="name" value={formData.name} onChange={handleChange} placeholder={formData.userType === 'company' ? 'np. BudPol Sp. z o.o.' : 'np. Jan Kowalski'} required data-testid="input-name" />
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="twoj@email.pl" required data-testid="input-email" />
              <p className="text-xs text-slate-500 mt-1">Na ten adres będą wysyłane powiadomienia i faktury</p>
            </div>
            <div>
              <Label htmlFor="password">Hasło</Label>
              <Input id="password" name="password" type="password" value={formData.password} onChange={handleChange} placeholder="Min. 6 znaków" required data-testid="input-password" />
            </div>
            <div>
              <Label htmlFor="confirmPassword">Potwierdź hasło</Label>
              <Input id="confirmPassword" name="confirmPassword" type="password" value={formData.confirmPassword} onChange={handleChange} placeholder="Powtórz hasło" required data-testid="input-confirm-password" />
            </div>

            <div className="space-y-3 pt-2 border-t border-slate-200">
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Zgody i regulaminy</p>
              <div className="flex items-start gap-3" data-testid="consent-terms">
                <input
                  type="checkbox"
                  id="terms"
                  checked={consents.terms_accepted}
                  onChange={(e) => setConsents({ ...consents, terms_accepted: e.target.checked })}
                  className="mt-1 h-4 w-4 rounded border-gray-300 accent-orange-500 cursor-pointer"
                />
                <label htmlFor="terms" className="text-sm text-slate-700 cursor-pointer">
                  Akceptuję <Link to="/regulamin" target="_blank" className="text-orange-600 hover:underline">Regulamin serwisu</Link> oraz{' '}
                  <Link to="/regulamin-platnosci" target="_blank" className="text-orange-600 hover:underline">Regulamin usług płatnych</Link> *
                </label>
              </div>
              <div className="flex items-start gap-3" data-testid="consent-privacy">
                <input
                  type="checkbox"
                  id="privacy"
                  checked={consents.privacy_accepted}
                  onChange={(e) => setConsents({ ...consents, privacy_accepted: e.target.checked })}
                  className="mt-1 h-4 w-4 rounded border-gray-300 accent-orange-500 cursor-pointer"
                />
                <label htmlFor="privacy" className="text-sm text-slate-700 cursor-pointer">
                  Akceptuję <Link to="/polityka-prywatnosci" target="_blank" className="text-orange-600 hover:underline">Politykę prywatności</Link> *
                </label>
              </div>
              <div className="flex items-start gap-3" data-testid="consent-marketing">
                <input
                  type="checkbox"
                  id="marketing"
                  checked={consents.marketing_accepted}
                  onChange={(e) => setConsents({ ...consents, marketing_accepted: e.target.checked })}
                  className="mt-1 h-4 w-4 rounded border-gray-300 accent-orange-500 cursor-pointer"
                />
                <label htmlFor="marketing" className="text-sm text-slate-700 cursor-pointer">
                  Wyrażam zgodę na otrzymywanie informacji marketingowych drogą elektroniczną (opcjonalne)
                </label>
              </div>
              <p className="text-xs text-slate-400">* Pola wymagane</p>
            </div>

            <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" disabled={loading} data-testid="submit-btn">
              {loading ? 'Tworzenie konta...' : 'Utwórz konto'}
            </Button>
          </form>

          {/* Google Login - tymczasowo wyłączone */}

          <p className="text-center text-sm text-slate-600 mt-6">
            Masz już konto? <Link to="/login" className="text-orange-600 hover:text-orange-700 font-medium">Zaloguj się</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
